<script setup lang="ts">
import { ref, type PropType } from 'vue';
import { DateTime } from 'luxon';
import { type FormField, type Signatory } from '@/models';
import { useUserStore } from '@/stores/user';
import { formattedDate, DATE_FORMAT_3 } from '@/utils';
import { ButtonType, CustomButton } from '@/components';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();

const userStore = useUserStore();

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  previewMode: {
    type: Boolean
  }
});

const localField = ref<FormField>(props.field);
const emit = defineEmits(['onRevoke']);

const onRevokeSignature = () => {
  const signIndex = localField.value.signatureValue?.signatories.findIndex(sign => sign.email === userStore.user?.email);
  if (signIndex && signIndex > -1) {
    localField.value.signatureValue!.signatories[signIndex].signature = '';
    localField.value.signatureValue!.signatories[signIndex].revoked = true;

    emit('onRevoke');
  }
};

const onRemoveSignature = (signature: Signatory) => {
  const signIndex = localField.value.signatureValue?.signatories.findIndex(sign => sign.email === signature.email);
  if (signIndex && signIndex > -1) {
        localField.value.signatureValue!.signatories[signIndex].signature = '';
        localField.value.signatureValue!.signatories[signIndex].removed = true;
  }
};
</script>

<template>
    <div class="list-root" v-if="localField.signatureValue">
        <div v-for="sign in localField.signatureValue.signatories" :key="sign.email">
            <div class="signatories" v-if="!sign.removed">
                <div class="email">
                    {{sign.signatory !== "" ? sign.signatory : sign.email}}
                    {{sign.email === userStore.user!.email ? `(${$t('you')})` : ""}}
                </div>
                <div class="status-container">
                    <div class="status" v-if="sign.emailSent && sign.signature == '' && !sign.revoked">
                        <FontAwesomeIcon :icon="['far', 'envelope']" class="icon envelope"/>
                        <div class="label">{{ sign.email  === userStore.user!.email ? $t('requested') : $t('request_sent') }}</div>
                    </div>
                    <div class="status signed" v-else-if="sign.signature != ''">
                        <FontAwesomeIcon :icon="['far', 'circle-check']" class="icon signed"/>
                        <div class="label signed-label">{{ $t('signed') }}</div>
                        <img :src="
                                sign.signature.includes('data:image/png;base64') ?
                                    sign.signature :
                                    `data:image/png;base64,${sign.signature}`"
                            alt="Captured signature"
                            class="signture-image-small"/>
                    </div>
                    <div class="status" v-else-if="sign.revoked">
                        <FontAwesomeIcon :icon="['far', 'circle-xmark']" class="icon revoked"/>
                        <div class="label">{{ $t('revoked_sign') }}</div>
                    </div>
                    <div class="status invitation" v-else>
                        {{ $t('save_send_invite') }}
                    </div>
                </div>
                <div class="date-time">{{ formattedDate(DateTime.fromSeconds(sign.dateTime).toJSDate(), DATE_FORMAT_3) }}</div>
                <div class="signature-actions">
                    <CustomButton :text="$t('revoke')"
                        class="revoke-button"
                        :margin-left="16"
                        @onClick="onRevokeSignature"
                        v-if="sign.signature !== '' && sign.email === userStore.user!.email && !previewMode"
                        :type="ButtonType.OUTLINED"/>
                    <CustomButton :text="$t('remove')"
                        class="revoke-button"
                        :margin-left="16"
                        @onClick="onRemoveSignature(sign)"
                        v-if="sign.signature === '' && sign.emailSent && !sign.revoked  && !previewMode"
                        :type="ButtonType.OUTLINED"/>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
@use '../../../../assets/variables.module' as *;
.list-root {
    padding: 12px 0px 4px 0px;
}
.signatories {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 8px 0px;
}
.email {
    flex: 1;
}
.status-container {
    flex: 1;
}
.status {
    display: flex;
    align-items: center;

    .icon {
        font-size: 16px;
    }

    .label {
        margin-left: 12px;
    }

    .envelope {
        color: $primary-color;
    }

    .signed {
        color: #189988;
    }

    .revoked {
        color: #ea3546;
    }

    .signture-image-small {
        height: 24px;
        margin-left: 12px;
    }
}
.date-time {
    flex: 1;
}
.signature-actions {
    flex: 1;
}
</style>
